export interface ObservedResource { scopeKey: string; kind: "benefit-application"; id: string }
export type ReadOutcome = "success" | "unavailable" | "accessDenied"
export interface ResourceFreshness extends ObservedResource {
  dirtyGeneration: number
  reconciledGeneration: number | null
  phase: "dirty" | "reading" | "current" | "unavailable" | "accessDenied"
}
export interface RefreshClock {
  now(): number
  schedule(callback: () => void, delay: number): () => void
}
interface RefreshPorts {
  /** Authoritative safe read only. Never supply a mutation/replay here. */
  read(resource: ObservedResource, signal: AbortSignal): Promise<ReadOutcome>
  changed(state: ResourceFreshness): void
  removed(resource: ObservedResource): void
  /** Use public RTK invalidation; unobserved records must not be eagerly fetched. */
  invalidateInactive(resource: ObservedResource): void
  /** Purge the protected view/draft/cache, not just its enabled buttons. */
  accessDenied(resource: ObservedResource): void
}
interface Entry {
  resource: ObservedResource
  refs: number
  suspended?: boolean
  dirty: number
  reconciled: number | null
  phase: ResourceFreshness["phase"]
  cancelTimer?: () => void
  read?: AbortController
}
const clock: RefreshClock = {
  now: () => Date.now(),
  schedule: (callback, delay) => { const timer = setTimeout(callback, delay); return () => clearTimeout(timer) },
}
const keyOf = (resource: ObservedResource) => JSON.stringify([resource.scopeKey, resource.kind, resource.id])

/**
 * Runtime-only timers, abort handles and bounded event dedupe. changed() emits
 * serializable freshness metadata, never DTOs or draft patches. A connected hub
 * is not proof of freshness: subscribe acknowledgements/focus/reconnect call dirty().
 */
export function createResourceRefreshCoordinator(ports: RefreshPorts, options: {
  clock?: RefreshClock; debounceMs?: number; eventTtlMs?: number; maxEventIds?: number
} = {}) {
  const time = options.clock ?? clock
  const delay = options.debounceMs ?? 150
  const ttl = options.eventTtlMs ?? 60_000
  const maxEvents = Math.max(1, options.maxEventIds ?? 256)
  const entries = new Map<string, Entry>()
  const events = new Map<string, number>()
  let scope: string | null = null
  let visible = true

  const emit = (entry: Entry) => ports.changed({ ...entry.resource, dirtyGeneration: entry.dirty,
    reconciledGeneration: entry.reconciled, phase: entry.phase })
  const active = (entry: Entry) => scope === entry.resource.scopeKey && entries.get(keyOf(entry.resource)) === entry

  function schedule(entry: Entry, wait = delay) {
    if (!visible || entry.suspended || entry.read || entry.cancelTimer || !active(entry)) return
    entry.cancelTimer = time.schedule(() => {
      entry.cancelTimer = undefined
      void refresh(entry)
    }, wait)
  }

  async function refresh(entry: Entry) {
    if (!visible || !active(entry) || entry.suspended || entry.read) return
    const generation = entry.dirty
    const controller = new AbortController()
    entry.read = controller
    entry.phase = "reading"
    emit(entry)
    let outcome: ReadOutcome
    try { outcome = await ports.read(entry.resource, controller.signal) }
    catch { outcome = "unavailable" }
    if (!active(entry) || controller.signal.aborted) return
    entry.read = undefined
    if (outcome === "accessDenied") {
      entry.suspended = true
      entry.phase = "accessDenied"
      emit(entry)
      ports.accessDenied(entry.resource)
      return
    }
    if (entry.dirty !== generation) {
      // An event after request start always requires one trailing read. Never
      // infer that an own-user/correlation echo proves the command succeeded.
      entry.phase = "dirty"
      emit(entry)
      schedule(entry)
    } else {
      entry.phase = outcome === "success" ? "current" : "unavailable"
      if (outcome === "success") entry.reconciled = generation
      emit(entry)
    }
  }

  function release(entry: Entry) {
    entries.delete(keyOf(entry.resource))
    entry.cancelTimer?.()
    entry.read?.abort()
    ports.removed(entry.resource)
  }

  return {
    /** Hidden workspaces retain dirty generations but start no new background reads. */
    setVisible(next: boolean) {
      visible = next
      for (const entry of entries.values()) {
        if (!next) { entry.cancelTimer?.(); entry.cancelTimer = undefined }
        else if (entry.phase === "dirty") schedule(entry)
      }
    },
    setScope(next: string | null) {
      if (scope === next) return
      scope = next
      for (const entry of entries.values()) release(entry)
      events.clear()
    },
    /** Only call after the session/resource authorization boundary accepts this observer. */
    watch(resource: ObservedResource): () => void {
      if (!scope || resource.scopeKey !== scope) throw new Error("Resource scope is not active.")
      const key = keyOf(resource)
      let entry = entries.get(key)
      if (entry) entry.refs++
      else {
        entry = { resource: { ...resource }, refs: 1, dirty: 0, reconciled: null, phase: "dirty" }
        entries.set(key, entry)
        emit(entry)
        schedule(entry, 0)
      }
      const watched = entry
      let released = false
      return () => {
        if (released) return
        released = true
        if (active(watched) && --watched.refs === 0) release(watched)
      }
    },
    /** A disconnected/unacknowledged subscription cannot authorize writes or safe-read completion. */
    suspend(resource: ObservedResource, denied = false) {
      const entry = entries.get(keyOf(resource))
      if (!entry || !active(entry)) return
      entry.suspended = true; entry.dirty++
      entry.cancelTimer?.(); entry.cancelTimer = undefined
      entry.read?.abort(); entry.read = undefined
      entry.phase = denied ? "accessDenied" : "unavailable"
      emit(entry)
      if (denied) ports.accessDenied(resource)
    },
    /** Only the matching, current join acknowledgment may resume reconciliation. */
    resume(resource: ObservedResource) {
      const entry = entries.get(keyOf(resource))
      if (!entry || !active(entry)) return
      entry.suspended = false; entry.dirty++; entry.phase = "dirty"
      emit(entry); schedule(entry)
    },
    dirty(resource: ObservedResource, eventId?: string) {
      if (!scope || resource.scopeKey !== scope) return false
      const key = keyOf(resource)
      if (eventId) {
        const now = time.now()
        for (const [id, at] of events) if (now - at >= ttl) events.delete(id)
        const eventKey = JSON.stringify([key, eventId])
        if (events.has(eventKey)) return false
        events.set(eventKey, now)
        while (events.size > maxEvents) events.delete(events.keys().next().value!)
      }
      const entry = entries.get(key)
      if (!entry) { ports.invalidateInactive(resource); return true }
      if (entry.phase === "accessDenied") return false
      entry.dirty++
      if (!entry.suspended) entry.phase = "dirty"
      emit(entry)
      schedule(entry)
      return true
    },
    dispose() {
      scope = null
      for (const entry of entries.values()) release(entry)
      events.clear()
    },
    /** Safe instrumentation only, no record or event identifiers. */
    counts: () => ({ observed: entries.size, recentEvents: events.size }),
  }
}
